import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

export const PERMISSION_LEVELS = {
  NONE: 'none',
  VIEW: 'view',
  EDIT: 'edit',
};

/** Calendar sections the tour owner can grant access to. */
export const CALENDAR_PERMISSION_TABS = [
  'TOUR PARTY',
  'RIDER',
  'STAGE',
  'SETLIST',
  'GUEST LIST',
  'SCHEDULE',
  'FILES',
  'CONTACTS',
  'MERCH',
  'NOTES',
  'DAY SHEET',
  'LODGING',
  'SETTLEMENT',
  'EXPENSES',
];

/** Shown in the permissions grid. STAGE stays stored so existing grants still apply. */
export const CALENDAR_PERMISSION_TABS_VISIBLE = CALENDAR_PERMISSION_TABS.filter((tab) => tab !== 'STAGE');

const VALID_LEVELS = new Set(Object.values(PERMISSION_LEVELS));

export function isTourOwner(userId, ownerId) {
  return Boolean(userId && ownerId && userId === ownerId);
}

export function resolveTourPermissionContext(folder, currentUid) {
  const ownerId = folder?.sharedFrom?.ownerId || folder?.ownerId || currentUid || null;
  const tourId = folder?.sharedFrom?.tourId || folder?.id || null;
  return { ownerId, tourId };
}

export function normalizeMemberPermissionsEntry(raw = {}) {
  const tabs = {};
  CALENDAR_PERMISSION_TABS.forEach((tabKey) => {
    const rawLevel = raw?.tabs?.[tabKey];
    const level = String(rawLevel || PERMISSION_LEVELS.VIEW).toLowerCase();
    tabs[tabKey] = VALID_LEVELS.has(level) ? level : PERMISSION_LEVELS.VIEW;
  });
  return {
    tabs,
    canManageParty: Boolean(raw?.canManageParty),
    canManageShares: Boolean(raw?.canManageShares),
  };
}

export function normalizeMemberPermissionsMap(raw = {}) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  Object.entries(raw).forEach(([userId, entry]) => {
    const uid = String(userId || '').trim();
    if (!uid) return;
    out[uid] = normalizeMemberPermissionsEntry(entry);
  });
  return out;
}

export function getMemberPermissionsEntry(settings, userId, managerIds = []) {
  const uid = String(userId || '').trim();
  if (!uid) return normalizeMemberPermissionsEntry({});
  const stored = settings?.memberPermissions?.[uid];
  const entry = normalizeMemberPermissionsEntry(stored || {});
  if ((managerIds || []).includes(uid)) {
    entry.canManageShares = true;
  }
  return entry;
}

/** Full access shown (read-only) for the tour creator. */
export function buildOwnerFullAccessEntry() {
  const tabs = {};
  CALENDAR_PERMISSION_TABS.forEach((tabKey) => {
    tabs[tabKey] = PERMISSION_LEVELS.EDIT;
  });
  return { tabs, canManageParty: true, canManageShares: true };
}

export function summarizeMemberPermissionTabs(entry) {
  let edit = 0;
  let view = 0;
  let hidden = 0;
  CALENDAR_PERMISSION_TABS_VISIBLE.forEach((tabKey) => {
    const level = entry?.tabs?.[tabKey] || PERMISSION_LEVELS.VIEW;
    if (level === PERMISSION_LEVELS.EDIT) edit += 1;
    else if (level === PERMISSION_LEVELS.VIEW) view += 1;
    else hidden += 1;
  });
  return { edit, view, hidden };
}

/**
 * Whether the tour owner can change this roster row's grants.
 * The creator is always locked; pending / off-platform people wait until they join.
 */
export function memberPermissionEditState(ownerId, member = {}) {
  const memberUid = String(member.id || '').trim();
  const status = String(member.status || 'accepted').toLowerCase();
  if (isTourOwner(memberUid, ownerId)) {
    return {
      memberUid,
      canEdit: false,
      readOnlyMessage:
        'You created this tour and always have full access. These settings stay locked so you cannot lock yourself out.',
    };
  }
  if (status !== 'accepted') {
    return {
      memberUid,
      canEdit: false,
      readOnlyMessage: 'Permissions can be configured after this member accepts their invitation.',
    };
  }
  if (!member.onPlatform || !memberUid) {
    return {
      memberUid,
      canEdit: false,
      readOnlyMessage: 'Permissions can be configured after this member joins on VRC with an account.',
    };
  }
  return { memberUid, canEdit: true, readOnlyMessage: '' };
}

export function canViewCalendarTab(userId, ownerId, tabKey, settings) {
  if (isTourOwner(userId, ownerId)) return true;
  const entry = getMemberPermissionsEntry(settings, userId, settings?.managerIds);
  const level = entry.tabs?.[tabKey] || PERMISSION_LEVELS.VIEW;
  return level === PERMISSION_LEVELS.VIEW || level === PERMISSION_LEVELS.EDIT;
}

export function canEditCalendarTab(userId, ownerId, tabKey, settings) {
  if (isTourOwner(userId, ownerId)) return true;
  const entry = getMemberPermissionsEntry(settings, userId, settings?.managerIds);
  return entry.tabs?.[tabKey] === PERMISSION_LEVELS.EDIT;
}

/**
 * Master Tour canvas -> permission keys. `view` gates the nav tab and the page;
 * `edit` lists every key a member needs EDIT on before the page lets them change data.
 * Canvases that are not listed (month, week, map, timeline, chat) stay open to everyone.
 */
export const V2_CANVAS_PERMISSION_KEYS = {
  riders: { view: 'RIDER', edit: ['RIDER'] },
  stage: { view: 'STAGE', edit: ['STAGE'] },
  setlist: { view: 'SETLIST', edit: ['SETLIST'] },
  guestlist: { view: 'GUEST LIST', edit: ['GUEST LIST'] },
  day: { view: 'SCHEDULE', edit: ['SCHEDULE'] },
  // The day sheet poster writes the SCHEDULE doc, so editing needs both grants.
  daysheet: { view: 'DAY SHEET', edit: ['DAY SHEET', 'SCHEDULE'] },
  contacts: { view: 'CONTACTS', edit: ['CONTACTS'] },
  merch: { view: 'MERCH', edit: ['MERCH'] },
  settle: { view: 'SETTLEMENT', edit: ['SETTLEMENT'] },
  expenses: { view: 'EXPENSES', edit: ['EXPENSES'] },
  lodging: { view: 'LODGING', edit: ['LODGING'] },
  files: { view: 'FILES', edit: ['FILES'] },
  notes: { view: 'NOTES', edit: ['NOTES'] },
  tourparty: { view: 'TOUR PARTY', edit: ['TOUR PARTY'] },
};

/** View / edit access for one Master Tour canvas. The tour owner always gets both. */
export function resolveV2CanvasAccess(userId, ownerId, canvasId, settings) {
  const keys = V2_CANVAS_PERMISSION_KEYS[canvasId];
  if (!keys || isTourOwner(userId, ownerId)) return { canView: true, canEdit: true };
  const canView = canViewCalendarTab(userId, ownerId, keys.view, settings);
  const canEdit = canView && keys.edit.every((tabKey) => canEditCalendarTab(userId, ownerId, tabKey, settings));
  return { canView, canEdit };
}

export function canManageTourParty(userId, ownerId, settings) {
  if (isTourOwner(userId, ownerId)) return true;
  return Boolean(getMemberPermissionsEntry(settings, userId, settings?.managerIds).canManageParty);
}

export function canManageTourShares(userId, ownerId, settings) {
  if (isTourOwner(userId, ownerId)) return true;
  if ((settings?.managerIds || []).includes(userId)) return true;
  return Boolean(getMemberPermissionsEntry(settings, userId, settings?.managerIds).canManageShares);
}

export function resolveSetlistModeratorId(ownerId, settings) {
  const stored = String(settings?.setlistModeratorId || '').trim();
  return stored || String(ownerId || '').trim() || null;
}

export function canPublishSetlist(userId, ownerId, settings) {
  const uid = String(userId || '').trim();
  if (!uid) return false;
  if (isTourOwner(uid, ownerId)) return true;
  const moderatorId = String(settings?.setlistModeratorId || '').trim();
  return Boolean(moderatorId && moderatorId === uid);
}

export async function saveSetlistModerator(db, ownerId, tourId, memberUid, currentSettings = {}) {
  if (!ownerId || !tourId) throw new Error('Missing tour.');
  const nextId = String(memberUid || '').trim();
  if (nextId && nextId === ownerId) {
    await setDoc(
      doc(db, 'users', ownerId, 'calendarShareSettings', tourId),
      { tourId, setlistModeratorId: '', updatedAt: serverTimestamp() },
      { merge: true }
    );
    return;
  }
  await setDoc(
    doc(db, 'users', ownerId, 'calendarShareSettings', tourId),
    {
      tourId,
      setlistModeratorId: nextId,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export function getViewableCalendarTabs(userId, ownerId, settings, allTabs = CALENDAR_PERMISSION_TABS) {
  if (isTourOwner(userId, ownerId)) return [...allTabs];
  return allTabs.filter((tabKey) => canViewCalendarTab(userId, ownerId, tabKey, settings));
}

export async function saveMemberPermissionEntry(db, ownerId, tourId, userId, entry, currentSettings = {}) {
  if (!ownerId || !tourId || !userId) throw new Error('Missing tour or member.');
  if (userId === ownerId) {
    throw new Error('Tour creator permissions cannot be changed.');
  }
  const normalized = normalizeMemberPermissionsEntry(entry);
  const memberPermissions = {
    ...normalizeMemberPermissionsMap(currentSettings.memberPermissions),
    [userId]: normalized,
  };

  let managerIds = [...(currentSettings.managerIds || [])];
  if (normalized.canManageShares) {
    if (!managerIds.includes(userId)) managerIds.push(userId);
  } else {
    managerIds = managerIds.filter((id) => id !== userId);
  }

  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  await setDoc(
    ref,
    {
      tourId,
      memberPermissions,
      managerIds,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
